import React, { useEffect } from 'react';
import { EpisodeData, SeriesData } from '../context/FileLibraryContext';
import Preloader from '../components/Preloader';
import { Chip, FormControl, Grid2, MenuItem, Paper, Select } from '@mui/material';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from "react-icons/io";
import VideoPlayerComponent from '../components/VideoPlayerComponent';
import AudioPlayerComponent from '../components/AudioPlayerComponent';
import { useAppContext } from '../context/AppContext';
import { useModal } from '../context/ModalContext';
import { IoLockClosedSharp } from "react-icons/io5";
import { IoMdDownload } from 'react-icons/io';

const ContentPlayerPage: React.FC = () => {
    const { accessTier, fileLibrary } = useAppContext();
    const { showModal } = useModal();
    const [ series, setSeries ] = React.useState<SeriesData | null>(null);
    const [ selectedEpisodeNumber, setSelectedEpisodeNumber ] = React.useState<number>(1);
    const [ episode, setSelectedEpisode ] = React.useState<EpisodeData | null>(null);

    const [ downloadMode, setDownloadMode ] = React.useState<string>("episode"); // episode or series
    const [ downloadVersion, setDownloadVersion ] = React.useState<number>(0); // 0 = first link, 1 = second link, etc.

    if (episode && downloadVersion >= episode.downloadLinks.length) {
        setDownloadVersion(0);
    }

    // get series ID from URL
    useEffect(() => {
        // get file ID and series ID from url (eg. /files/seriesId/episodeId)
        const urlParts = window.location.pathname.split('/');
        const seriesId = urlParts[2];
        const series = fileLibrary?.series.find((s: SeriesData) => s.id === seriesId);

        const episodeId = urlParts.length > 2 && urlParts[3] || "";
        // find episode in series with matching episode ID
        let episodeNum = parseInt(series?.episodes.find((e: EpisodeData) => e.id === episodeId)?.id || "1");
        let episode = series?.episodes.find((e: EpisodeData) => e.episode === episodeNum) || series?.episodes[0];

        if (!series || !episode) {
            //toast.error("There was a problem finding this content.");
            return;
        }

        setSeries(series);
        setEpisode(episode, episodeNum);
        updateDocumentTitle(series, episode);

    }, [ fileLibrary ]);

    const setEpisode = (episode: EpisodeData, episodeNumber: number) => {
        setSelectedEpisode(episode);
        setSelectedEpisodeNumber(episodeNumber);
    }

    const updateDocumentTitle = (series: SeriesData, episode: EpisodeData) => {
        document.title = `${episode.name} | ipwd.club`;
        // set url to /files/seriesId/episodeId
        window.history.pushState({}, '', `/files/${series.id}/${episode.id}`);
    }

    const handleSetEpisodeClick = (epNumber: number) => {
        const episodeData = series?.episodes.find((e: EpisodeData) => e.episode === epNumber);

        if (!episodeData) {
            toast.error("Episode not found!");
            return;
        }

        setEpisode(episodeData, epNumber);
        updateDocumentTitle(series!, episodeData);
    }

    if (!fileLibrary) {
        return <Preloader message='Fetching content...' />;
    }

    if (!series) {
        return <div className="center-div"><p className="error">Series not found!</p></div>;
    }

    if (!episode && (series.episodes && series.episodes.length > 0)) {
        return <div className="center-div"><p className="error">Episode not found!</p></div>;
    }

    // make sure user has access to this series and episode
    if (series.seriesTier > accessTier) {
        return <div className="center-div"><p className="error">You do not have access to this series.</p></div>;
    }
    if (episode && episode.accessTier > accessTier) {
        return <div className="center-div"><p className="error">You do not have access to this episode.</p></div>;
    }

    function generateEpisodeListDropdownJsx() {
        if (series?.episodes.length === 1) {
            return <></>;
        }

        return (
            <FormControl variant='standard' sx={{ m: 1, minWidth: 80, marginTop: 0 }}>
              <Select
                labelId="demo-simple-select-autowidth"
                id="demo-simple-select-autowidth"
                value={selectedEpisodeNumber}
                onChange={(e) => handleSetEpisodeClick(e.target.value as number)}
                autoWidth
              >
                { series?.episodes.map((episode: EpisodeData) => (
                    <MenuItem key={episode.episode} disabled={episode.accessTier > accessTier} value={episode.episode}>{episode.episode} - {episode.name}</MenuItem>
                )) }
              </Select>
            </FormControl>
        )
    }

    function buildDownloadSelectJsx() {
        const jsx: JSX.Element[] = [];

        if (series!.seriesLinks?.length > 0) {
            jsx.push(
                <Grid2 size={{xs: 12, sm: "grow"}} style={{textAlign: "center"}}>
                    <FormControl variant="standard">
                        <Select labelId="download-mode" 
                            id="download-mode" 
                            value={downloadMode} 
                            onChange={(e) => setDownloadMode(e.target.value as string)}>
                            <MenuItem value="episode">This Episode</MenuItem>
                            <MenuItem value="series">Full Series</MenuItem>
                        </Select>
                    </FormControl>
                </Grid2>
            )
        }

        const menuItems: JSX.Element[] = [];

        if (downloadMode === "series") {
            
            for (let i = 0; i < series!.seriesLinks.length; i++) {
                const linkData = series!.seriesLinks[i];
                const isDisabled = !linkData.url.includes("token");
                menuItems.push(
                    <MenuItem key={i} value={i} disabled={isDisabled}>
                        {linkData.title}
                    </MenuItem>
                );
            }
        }
        else {
            for (let i = 0; i < episode!.downloadLinks.length; i++) {
                const linkData = episode!.downloadLinks[i];
                const isDisabled = !linkData.url.includes("token");
                menuItems.push(
                    <MenuItem key={i} value={i} disabled={isDisabled}>
                        {linkData.title}
                    </MenuItem>
                );
            }
        }

        jsx.push(<Grid2 size={{xs: 12, sm: "grow"}} style={{textAlign: "center"}}>
            <FormControl variant="standard">
                <Select labelId="download-version" 
                    id="download-version" 
                    value={downloadVersion} 
                    onChange={(e) => setDownloadVersion(e.target.value as number)}>
                    { menuItems }
                </Select>
            </FormControl>
        </Grid2>);

        return jsx;
    }

    const generateDownloadLinkJsx = () => {
        let downloadUrl = "";
        if (downloadMode === "series") {
            downloadUrl = series!.seriesLinks[downloadVersion].url;
        } else {
            if (!episode) {
                return <></>;
            }
            downloadUrl = episode!.downloadLinks[downloadVersion].url;
            if (episode.type === "audio") {
                downloadUrl = downloadUrl + "&forceDownload=true";
            }
        }
        console.log("downloadUrl: ", downloadUrl);
        return (<a href={downloadUrl} download className="btn download-btn"><IoMdDownload /> Download</a>);
    }

    return (
        <>
            <div className="breadcrumbs">
                <Link to="/files"><IoMdArrowRoundBack /> Files</Link>
            </div>
            <div className="center-div video-player-page">
                <h1>{series.name}</h1>
                {
                    series.episodes.length > 1 ?
                    <p style={{fontSize: "1.2em", color: "#976eff", marginBottom: "0" }}>
                        <b>Select episode:</b>
                    </p> 
                    : <></>
                }
                { generateEpisodeListDropdownJsx() }
                <p className="video-flavorText">{episode?.flavorText}</p>
                {
                    episode ? episode?.type === "audio" ?
                        <AudioPlayerComponent series={series} episode={episode} />
                        :
                        <VideoPlayerComponent series={series} episode={episode!} />
                        :
                        <p></p>
                        // :
                        // <div className="center-div" style={{minHeight: "300px"}}>
                        //     <p>
                        //         <IoLockClosedSharp size={40} style={{color: "#976eff"}} />
                        //     </p>
                        //     <p className="error">
                        //         This episode is only available to <b>Deeper</b> tier patrons.
                        //     </p>
                        //     <p>
                        //         <a href="https://www.patreon.com/checkout/iplaywithdolls?rid=22386333" target="_blank" rel="noopener noreferrer" className="patreon-button">Click here to upgrade</a>
                        //     </p>
                        // </div>
                }
            
                <Grid2 container spacing={2} style={{ alignItems: "center", width: "100%" }}>
                    <Grid2 size={{xs: 12, sm: "grow"}} style={{color: "#b397f3", fontSize: "1.3em", fontStyle: "bold", textAlign: "center" }}>
                        Download:
                    </Grid2>
                    { buildDownloadSelectJsx() }
                    <Grid2 size={{xs: 12, sm: "grow"}} style={{textAlign: "center"}}>
                        { generateDownloadLinkJsx() }
                    </Grid2>
                </Grid2>

                <p></p>
                <Paper elevation={3}>
                    <div className="video-details">
                        { series!.flavorText ? <p className="video-flavorText" dangerouslySetInnerHTML={{ __html: "\"" + series!.flavorText + "\"" }} /> : <></> }
                        { series!.description ? <p className="video-description">{series!.description}</p> : <></> }
                        { 
                            <Grid2 container spacing={1} style={{width: "100%"}}> 
                                <Grid2 size={{xs: 12}}><b>Content warnings:</b></Grid2> 
                                {series!.cw.map((cw) => { return <Grid2><Chip label={cw} color="error" variant="outlined" /></Grid2>})} 
                            </Grid2>
                        }
                        <p></p>
                        { 
                            <Grid2 container spacing={1} style={{width: "100%"}}> 
                                <Grid2 size={{xs: 12}}><b>Tags:</b></Grid2> 
                                {series!.tags.map((tag) => { return <Grid2><Chip label={tag} color="primary" variant="outlined" /></Grid2>})} 
                            </Grid2>
                        }
                    </div>
                </Paper>
            </div>
        </>
    );
};

export default ContentPlayerPage;